import { getAccessToken } from './auth';

const BASE_URL = import.meta.env.VITE_API_URL || '/api';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: any,
    /** Full response body (code/needed/available/etc.). `details` stays the
     *  guardrail violations array for back-compat with existing callers. */
    public body?: any,
  ) {
    super(message);
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // Agency out-of-credits — broadcast so the CreditProvider can open the
    // top-up modal with a recommended pack (decoupled: api.ts is not a component).
    if (body?.code === 'INSUFFICIENT_CREDITS') {
      window.dispatchEvent(
        new CustomEvent('traaker:insufficient-credits', {
          detail: { needed: body.needed, available: body.available },
        }),
      );
    }
    // Expired trial (402 from the BillingGuard). The cached tenant snapshot may
    // still say 'trialing' with a future-looking UI; broadcast so AuthContext
    // refreshes the profile and DashboardLayout shows the paywall overlay.
    if (res.status === 402 && body?.code === 'TRIAL_EXPIRED') {
      window.dispatchEvent(new CustomEvent('traaker:trial-expired'));
    }
    // details = guardrail violations (back-compat); body = full payload.
    throw new ApiError(res.status, body.message || `Request failed: ${res.status}`, body.violations, body);
  }

  // Some endpoints return 204 or an empty body — parse safely.
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
}

/** Authenticated fetch that returns the raw response as a Blob (CSV/PDF export). */
async function requestBlob(path: string): Promise<Blob> {
  const token = await getAccessToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${BASE_URL}${path}`, { headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.message || `Request failed: ${res.status}`, body.violations, body);
  }
  return res.blob();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  getBlob: (path: string) => requestBlob(path),
  post: <T>(path: string, body: unknown) => request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'DELETE',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    }),
};
